import { Stack } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import Feed from "@/components/feed/Feed";
import Loading from "@/components/Loading/Index";
import NoRightsToViewContentModal from "@/components/NoRightsToViewContentModal.tsx";
import { AppContext } from "@/models/AppStateProvider";
import FeedContextProvider, { FeedContext } from "@/models/FeedContextProvider";
import { invalidFeedPermissions } from "@/models/queryTransformers.ts";
import { CurrentFeedContext } from "@/models/StateProviders/currentFeedProvider";
import { UserContext } from "@/models/StateProviders/userProvider.tsx";
import { actions, TelemetryContext } from "@/models/TelemetryProvider";

function FeedIdInner() {
  const { currentFeedId } = useContext(CurrentFeedContext);
  const { feedMap, currentFeed } = useContext(FeedContext);
  const { finishAction } = useContext(TelemetryContext);

  useEffect(() => {
    if (currentFeedId && currentFeed && feedMap !== undefined) {
      const hasLoaded = currentFeed.loadedLastPage || currentFeed.loadedFirstPage;
      if (hasLoaded && feedMap.length === 0) {
        finishAction(actions.feedNavigate(currentFeedId));
      }
    }
  }, [currentFeedId, currentFeed, feedMap, finishAction]);

  return null;
}

export default function FeedId({ type }: { type: "legacy" | "drivers" | "channels" }) {
  const { currentFeedId, currentFeed } = useContext(CurrentFeedContext);
  const [renderToggle, setRenderToggle] = useState<string>("");
  const { workspaceMembership } = useContext(UserContext);
  const { flags } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId, type: urlType } = useParams();

  useEffect(() => {
    if (history.scrollRestoration) {
      history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    if (type !== "legacy" || urlType !== "feeds" || !flags.newDesign) return;
    if (!currentFeed || !workspaceId || !currentFeedId) return;

    // Use the router location for the fragment — in Electron, `window.location.hash`
    // can lag behind React Router right after programmatic navigation, which would
    // drop the deeplink when replacing /feeds/ with /channels/ or /drivers/.../channel/.
    const hash = location.hash.length > 0 ? location.hash : undefined;
    if (currentFeed.aliasMembershipId) {
      navigate(
        {
          pathname: `/workspaces/${workspaceId}/drivers/${currentFeed.aliasMembershipId}/channel/${currentFeedId}`,
          ...(hash ? { hash } : {}),
        },
        { replace: true },
      );
    } else {
      navigate(
        {
          pathname: `/workspaces/${workspaceId}/channels/${currentFeedId}`,
          ...(hash ? { hash } : {}),
        },
        { replace: true },
      );
    }
  }, [type, urlType, flags.newDesign, currentFeed, workspaceId, currentFeedId, navigate, location.hash]);

  const keyForContext = type;

  const showComponent = invalidFeedPermissions({
    workspaceMembershipId: workspaceMembership?.id,
    feedId: currentFeedId,
  });

  if (showComponent === "NoRightsToViewContentModal") {
    return <NoRightsToViewContentModal open={true} />;
  }

  if (!currentFeed || showComponent === "Loading") {
    return (
      <Stack
        sx={{
          width: "100%",
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loading />
      </Stack>
    );
  }

  return (
    <FeedContextProvider key={keyForContext}>
      <FeedIdInner />
      <Feed setRenderToggle={setRenderToggle} data-testid={`feedId-${renderToggle}`} />
    </FeedContextProvider>
  );
}
